import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useScrollY } from '../../hooks/useScrollY';
import { useScrollDirection } from '../../hooks/useScrollDirection';

const navLinks = [
  { label: 'Main',    to: '/'        },
  { label: 'Work',    to: '/work'    },
  { label: 'Log',     to: '/log'     },
  { label: 'Contact', to: '/contact' },
];

export default function GlobalHeader({
  hideThreshold = 80,
  overlay       = false,
  hamburgerOnly = false,
  heroMode      = false,  // 이미지 영역 위=흰색 / 벗어나면=검은색
}) {
  const scrollY   = useScrollY();
  const direction = useScrollDirection();
  const location  = useLocation();

  const [hiddenBySubNav, setHiddenBySubNav] = useState(false);
  const [mobileOpen, setMobileOpen]         = useState(false);

  useEffect(() => {
    const handler = (e) => setHiddenBySubNav(e.detail.isStuck);
    window.addEventListener('article-subnav-sticky', handler);
    return () => window.removeEventListener('article-subnav-sticky', handler);
  }, []);

  useEffect(() => {
    setHiddenBySubNav(false);
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const isHidden =
    hiddenBySubNav ||
    (scrollY > hideThreshold && direction === 'down');

  // heroMode: 뷰포트 높이 85% 지점까지는 "이미지 위" 로 판단
  const onHero = heroMode && scrollY < window.innerHeight * 0.85;
  // 열린 상태면 항상 다크
  const lineColor = (onHero && !mobileOpen) ? '#ffffff' : 'var(--c-n800)';

  const makeHamburger = (large = false) => {
    const btnCls  = large ? 'hamburger-btn hamburger-btn--lg' : 'hamburger-btn';
    const lineCls = large ? 'hamburger-line hamburger-line--lg' : 'hamburger-line hamburger-line--sm';
    const topOpen = large ? 'hamburger-line--top-open-lg' : 'hamburger-line--top-open';
    const botOpen = large ? 'hamburger-line--bot-open-lg' : 'hamburger-line--bot-open';
    return (
      <button
        onClick={() => setMobileOpen(v => !v)}
        aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
        className={btnCls}
      >
        <span
          className={`${lineCls} ${mobileOpen ? topOpen : 'hamburger-line--top-closed'}`}
          style={{ background: lineColor }}
        />
        <span
          className={`${lineCls} ${mobileOpen ? 'hamburger-line--mid-open' : 'hamburger-line--mid-closed'}`}
          style={{ background: lineColor }}
        />
        <span
          className={`${lineCls} ${mobileOpen ? botOpen : 'hamburger-line--bot-closed'}`}
          style={{ background: lineColor }}
        />
      </button>
    );
  };

  return (
    <>
      {!hamburgerOnly && (
        <header
          className={`g-hdr-sidebar${isHidden ? ' g-hdr-sidebar--hidden' : ''}`}
          style={{
            background: overlay ? 'transparent' : undefined,
            borderRight: overlay ? 'none' : undefined,
          }}
        >
          <nav className="g-hdr-sidebar-nav">
            {navLinks.map((link) => {
              const isActive =
                link.to === '/'
                  ? location.pathname === '/'
                  : location.pathname.startsWith(link.to);
              return (
                <div key={link.to} className="g-hdr-sidebar-item">
                  <Link
                    to={link.to}
                    className={isActive ? 'g-hdr-sidebar-link sidebar-link sidebar-link--active' : 'g-hdr-sidebar-link sidebar-link'}
                    style={{ fontSize: '1.125rem', letterSpacing: '0.01em', lineHeight: 1 }}
                  >
                    {link.label}
                  </Link>
                  {isActive && <div className="g-hdr-sidebar-active-bar" />}
                </div>
              );
            })}
          </nav>
        </header>
      )}

      <header
        className={`g-hdr-mob-header${hamburgerOnly ? ' g-hdr-mob-header--desktop' : ''}`}
        style={{ background: heroMode ? 'transparent' : undefined }}
      >
        {makeHamburger(hamburgerOnly)}
      </header>

      <div className={`g-hdr-overlay${hamburgerOnly ? ' g-hdr-overlay--always' : ''}${mobileOpen ? ' g-hdr-overlay--open' : ''}`}>
        <div className={`g-hdr-overlay-top${hamburgerOnly ? ' g-hdr-overlay-top--lg' : ''}`}>
          {makeHamburger(hamburgerOnly)}
        </div>
        <nav className="g-hdr-overlay-nav">
          {navLinks.map((link) => {
            const isActive =
              link.to === '/'
                ? location.pathname === '/'
                : location.pathname.startsWith(link.to);
            return (
              <Link
                key={link.to}
                to={link.to}
                className={`g-hdr-overlay-link${isActive ? ' g-hdr-overlay-link--active' : ''}`}
              >
                {link.label}
                {isActive && <span className="g-hdr-overlay-dot" />}
              </Link>
            );
          })}
        </nav>
      </div>
    </>
  );
}
